
export const SESSION_EVENTS = {
  START: 'SESSION_START',                    // User entered workspace (includes window dimensions)
  END: 'SESSION_END',                        // TODO
  TASK_COMPLETED: 'TASK_COMPLETED',          // All test cases passed (includes code snapshot)
  TASK_TIMEOUT: 'TASK_TIMEOUT',              // Timer expired before completion (includes code and test results)
}

export const GLOBAL_EVENTS = {
  MOUSE_MOVE: 'MOUSE_MOVE',                  // Mouse position (throttled 50ms)
  MOUSE_CLICK: 'MOUSE_CLICK',                // User clicked in workspace
  TAB_STATE: 'TAB_STATE',                    // User switched browser tabs (visible/hidden)
  WINDOW_RESIZE: 'WINDOW_RESIZE',            // User resized the browser window
  PANEL_RESIZE: 'PANEL_RESIZE',              // User resized a workspace panel
}

export const PREVIEW_EVENTS = {
  RENDER_REQUESTED: 'PREVIEW_RENDER_REQUESTED',
  RENDERED: 'PREVIEW_RENDERED',
  RELOAD: 'PREVIEW_RELOAD',
  ERROR: 'PREVIEW_RUNTIME_ERROR',
  CLICK: 'PREVIEW_CLICK',                    // User clicked inside the rendered output
  INPUT: 'PREVIEW_INPUT',                    // User typed/toggled a form element inside the rendered output
  SCROLL: 'PREVIEW_SCROLL',                  // User scrolled inside the rendered output (debounced 200ms)
  SUBMIT: 'PREVIEW_SUBMIT',                  // A <form> inside the rendered output was submitted
  FOCUS: 'PREVIEW_FOCUS',                    // Focus entered/left an element inside the rendered output
  CONSOLE: 'PREVIEW_CONSOLE',                // Student code called console.log/warn/error/info
  SNAPSHOT: 'PREVIEW_SNAPSHOT',              // Screenshot + DOM snapshot of the rendered output (visualTraceEnabled only)
  VIEWPORT_CHANGE: 'PREVIEW_VIEWPORT_CHANGE',// Student toggled the preview's simulated viewport width
}

export const REGIONS = {
  CODE_EDITOR: 'CODE_EDITOR',                // Code editor panel
  CHAT_WINDOW: 'CHAT_WINDOW',                // AI chat panel
  TERMINAL: 'TERMINAL',                      // Terminal output panel
  TASK_DESCRIPTION: 'TASK_DESCRIPTION',      // Task description section
  PROVIDED_CODE: 'PROVIDED_CODE',            // The exercise's read-only provided code (E3's Person)
  TEST_CASES: 'TEST_CASES',                  // Test cases section
  BACKGROUND: 'workspace_background',        // Default/fallback region
}

export const TASK_EVENTS = {
  SELECT: 'TASK_SELECT',                     // User highlighted text in task description
  COPY: 'TASK_COPY',                         // User copied from task description
}

export const PROVIDED_EVENTS = {
  SELECT: 'PROVIDED_SELECT',                 // User highlighted text in the provided code
  COPY: 'PROVIDED_COPY',                     // User copied from the provided code
}

export const TEST_EVENTS = {
  SELECT: 'TEST_SELECT',                     // User highlighted text in test cases
  COPY: 'TEST_COPY',                         // User copied from test cases
}

export const CODE_EVENTS = {
  TYPE: 'CODE_TYPE',                         // User typed characters
  SELECT: 'CODE_SELECT',                     // User highlighted text
  DELETE_SELECTION: 'CODE_DELETE_SELECTION', // User deleted highlighted text
  COPY: 'CODE_COPY',                         // User copied to clipboard (Ctrl+C)
  PASTE: 'CODE_PASTE',                       // User pasted content (Ctrl+V)
  DELETE: 'CODE_DELETE',                     // User deleted with Backspace or Delete key
  CUT: 'CODE_CUT',                           // User cut to clipboard (Ctrl+X)
  UNDO: 'CODE_UNDO',                         // User undid action (Ctrl+Z)
  REDO: 'CODE_REDO',                         // User redid action (Ctrl+Y / Ctrl+Shift+Z)
  INDENT: 'CODE_INDENT',                     // User indented code (Tab)
  UNKNOWN: 'CODE_UNKNOWN',                   // Unrecognized edit action
  TAB_SWITCH: 'CODE_TAB_SWITCH',             // User switched between HTML/CSS/JS editor tabs
}

export const TERMINAL_EVENTS = {
  RUN: 'TERMINAL_RUN',                       // User clicked Run Tests
  OUTPUT: 'TERMINAL_OUTPUT',                 // Python printed to stdout
  RESULT: 'TEST_CASE_RESULT',                // Test execution finished with results
  ERROR: 'TERMINAL_ERROR',                   // Code threw an error (includes type: SyntaxError, TypeError, etc.)
  SELECT: 'TERMINAL_SELECT',                 // User highlighted terminal output
  COPY: 'TERMINAL_COPY',                     // User copied terminal output
  NOTE: 'TERMINAL_NOTE',                     // The terminal showed the student a note (study/printNote.js)
}

export const CHAT_EVENTS = {
  TYPE: 'CHAT_TYPE',                         // User typed in chat input
  SELECT_INPUT: 'CHAT_SELECT_INPUT',         // User highlighted text in chat input
  SELECT_HISTORY: 'CHAT_SELECT_HISTORY',     // User highlighted text in chat history
  COPY_INPUT: 'CHAT_COPY_INPUT',             // User copied from chat input
  COPY_HISTORY: 'CHAT_COPY_HISTORY',         // User copied from chat history
  PASTE: 'CHAT_PASTE',                       // User pasted into chat input
  DELETE: 'CHAT_DELETE',                     // User deleted text (Backspace/Delete)
  QUERY: 'CHAT_QUERY',                       // User sent message to AI
  RESPONSE: 'CHAT_RESPONSE',                 // AI response received
  RESPONSE_READ: 'CHAT_RESPONSE_READ',       // Time between an AI response and the student's next action (reading_time_ms)
  ERROR: 'CHAT_ERROR',                       // AI API error
  ATTACHMENT: 'CHAT_ATTACHMENT',             // Student attached a screenshot/frame-sequence to their query
}

// The experiment offloading strip above the chat input (OffloadingStrip.jsx).
export const OFFLOADING_EVENTS = {
  CONCEPT_VIEW: 'OFFLOADING_CONCEPT_VIEW',   // Student opened a concept's tooltip
  LEARN_MORE: 'OFFLOADING_LEARN_MORE',       // Student pressed "Learn more" on a concept
}

// Study conditions only (studyConfig.js), from CodeEditor.jsx. Deliberately not CODE_*:
// dashboards count every CODE_* event as an edit, and these change no text.
export const EDITOR_EVENTS = {
  CURSOR_SELECT: 'CURSOR_SELECT',            // Cursor/selection moved without an edit (debounced 150ms): { ranges: [{ anchor, head }], main }
  KEY: 'EDITOR_KEY',                         // Non-text key in the editor (Enter, Tab, arrows, Ctrl/Cmd/Alt combos...): { key, ctrl, meta, shift, alt, repeat }
}

// Study conditions only, from TelemetryContext: whether the page and its connections were
// alive, so missing events can be told apart from a student doing nothing.
export const SYSTEM_EVENTS = {
  HEARTBEAT: 'HEARTBEAT',                    // Every 15s while the page is open: { visible, online, queuedEvents, lastSeq }
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',        // The browser went offline (window 'offline')
  NETWORK_ONLINE: 'NETWORK_ONLINE',          // The browser came back online (window 'online')
  SOCKET_DISCONNECT: 'SOCKET_DISCONNECT',    // The shared socket.io connection dropped: { reason }
  SOCKET_RECONNECT: 'SOCKET_RECONNECT',      // ...and reconnected: { attempt }
  SYNC_FAILED: 'TELEMETRY_SYNC_FAILED',      // A batch failed to save: { failedBatches, queuedEvents, status, droppedEvents }
  SYNC_RECOVERED: 'TELEMETRY_SYNC_RECOVERED',// Every waiting batch saved after a failure: { resentBatches }
}

// Study conditions only, from the probe modal (study/ProbeModal.jsx), the AI chat gate
// (study/useAiGate.js), the study phase (study/useStudyPhase.js) and the instructions and
// survey around it (study/StudyInstructions.jsx, study/StudySurvey.jsx).
export const STUDY_EVENTS = {
  PROBE_SHOWN: 'PROBE_SHOWN',                // A concept probe item appeared
  PROBE_ANSWERED: 'PROBE_ANSWERED',          // The student chose an option for a probe item
  PROBES_COMPLETED: 'PROBES_COMPLETED',      // Every probe item has an answer
  PROBE_REVIEW_SHOWN: 'PROBE_REVIEW_SHOWN',  // The answers with their explanations appeared, after the last answer: { items: [{ itemId, choice, correct }] }
  AI_UNLOCKED: 'AI_UNLOCKED',                // A delayed condition's AI chat column appeared, its time having come while the page was open: { unlockAtMs, serverNowMs, serverClock }
  AI_AVAILABILITY: 'AI_AVAILABILITY',        // Once per page: whether the AI chat column is shown: { panelShown, unlockAtMs, aiDelaySeconds, serverClock }
  INSTRUCTIONS_SHOWN: 'INSTRUCTIONS_SHOWN',  // The instructions after the consent form appeared (once per session and student): { durationMinutes, notice } (notice: the note about a changed AI access they opened with, 'ai-removed' | 'ai-later', or null)
  INSTRUCTIONS_NOTICE_SHOWN: 'INSTRUCTIONS_NOTICE_SHOWN', // That note appeared in the instructions after they did (the server's answer came late): { notice }
  PROBES_TRIGGERED: 'PROBES_TRIGGERED',      // Once per page, when the probes start: { reason: 'passed-all' | 'time-up', resumed } (resumed: the page opened after the start)
  SURVEY_SHOWN: 'SURVEY_SHOWN',              // The short survey on the Thank You overlay appeared
  SURVEY_SUBMITTED: 'SURVEY_SUBMITTED',      // The student submitted it: { rating, feedbackLength } (never the text)
  CONTINUED_WORKING: 'CONTINUED_WORKING',    // "Keep working on the exercise": the workspace again; { codingOver } - the condition's AI only while the time is not up
}

// The Code Coach substudy's chat (study/cc/CcChat.jsx; CC-O and CC-M conditions only), in place of
// CHAT_QUERY / CHAT_RESPONSE: the server keeps the exchange itself (backend ccTutor), these say what
// the page did and showed. Typing in its boxes is logged as CHAT_TYPE, CHAT_PASTE... as in every
// chat. roundId is the server's help round, clientRequestId the call an event is about.
export const CC_EVENTS = {
  REQUEST_SENT: 'CC_REQUEST_SENT',           // The student sent a new question: { clientRequestId, text, length, codeHash, runId }
  MESSAGE_RENDERED: 'CC_MESSAGE_RENDERED',   // A Code Coach or Evidence Checker message appeared: { roundId, messageId, role, text, restored } (restored: from the server's state, e.g. after a reload - not a reply to this page's call)
  CARD_SHOWN: 'CC_CARD_SHOWN',               // The "Your prediction" card appeared (CC-M only): { roundId, messageId, prompt, restored }
  CONTRIBUTION_SUBMITTED: 'CC_CONTRIBUTION_SUBMITTED', // The student answered the card: { roundId, clientRequestId, text, length, via: 'card' | 'chat', codeHash, runId }
  SKIPPED: 'CC_SKIPPED',                     // "I'm not sure—continue": { roundId, clientRequestId, codeHash, runId }
  ROUND_CANCELLED: 'CC_ROUND_CANCELLED',     // "Start a new question" pressed while the card waited: { roundId }
  FAILED: 'CC_FAILED',                       // A call to the tutor failed: { stage, roundId, clientRequestId, status, reason } (status null: no answer came)
  RETRY: 'CC_RETRY',                         // "Try again": { stage, roundId, clientRequestId, retryOf } (retryOf: the failed call's id - the same id for a question resent; for the checker asked again, the call whose reply said it failed, null when that came with the server's state after a reload)
}
